import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { BloomreachApi, parsePageModel, getPageRoot, defaultComponentDefinitions, createConfiguration, BrConfigurationProvider, BrPage, CmsContainerPage } from '@grandvisionhq/bloomreach';
import { LocaleUtils } from '@grandvisionhq/utils';
import { isPreview } from '../utils.js';

/**
 * Sets up the BloomreachProvider for any page and makes sure a pageModel is supplied.
 * This currently relies on our express servers to set the pageModel on the `res.locals` object.
 *
 * wrapInContainerPage: boolean Some pages are not container pages, like content and error pages
 *
 * @usage
 * First re-export from the opco site
 * const settings = {...}
 * export const withCms = withBloomreach(settings)
 *
 * const Page = () => {}
 * export default withCms()(Page)
 *
 * For non container pages
 * const Page = () => {}
 * export default withCms({ wrapInContainerPage: false })(Page)
 */ function withBloomreach(settings) {
    return ({ wrapInContainerPage  } = {
        wrapInContainerPage: true
    })=>{
        return (PageComponent)=>{
            const WithBloomreach = (pageProps)=>{
                const { pageModel , visitor , isMultilingualEnabled =false , locale , isContentPage , path ,  } = pageProps;
                const { bloomreach , defaultLocale  } = settings;
                var _componentDefinitions;
                const componentDefinitions = (_componentDefinitions = bloomreach.componentDefinitions) !== null && _componentDefinitions !== void 0 ? _componentDefinitions : defaultComponentDefinitions;
                const configurationSettings = {
                    apiUrl: bloomreach.apiUrl,
                    componentDefinitions,
                    customComponentMap: bloomreach.customComponentMap
                };
                const config = React.useMemo(()=>createConfiguration({
                        apiUrl: bloomreach.apiUrl,
                        path: path || '/',
                        options: {
                            isMultilingual: isMultilingualEnabled,
                            locale: locale !== null && locale !== void 0 ? locale : defaultLocale,
                            visitor
                        }
                    })
                , [
                    bloomreach,
                    path,
                    locale,
                    defaultLocale,
                    visitor,
                    isMultilingualEnabled
                ]);
                const shouldWrap = !isContentPage && wrapInContainerPage;
                const Wrapper = shouldWrap ? CmsContainerPage : React.Fragment;
                return /*#__PURE__*/ React.createElement(BrConfigurationProvider, {
                    settings: configurationSettings
                }, /*#__PURE__*/ React.createElement(BrPage, {
                    mapping: componentDefinitions,
                    page: pageModel,
                    configuration: config
                }, /*#__PURE__*/ React.createElement(Wrapper, null, /*#__PURE__*/ React.createElement(PageComponent, swcHelpers.extends({}, pageProps, {
                    isContentPage: isContentPage
                })))));
            };
            if (process.env.NODE_ENV !== 'production') {
                const displayName = PageComponent.displayName || PageComponent.name || 'Unknown';
                WithBloomreach.displayName = `WithBloomreach(${displayName})`;
            }
            WithBloomreach.getInitialProps = function() {
                var _ref = swcHelpers.asyncToGenerator(function*(ctx) {
                    var ref, ref1;
                    const { req , asPath ='' , res , pageModel  } = ctx;
                    const { defaultLocale , isMultilingualEnabled  } = settings;
                    const { apiUrl  } = settings.bloomreach;
                    // @ts-expect-error - Somehow `query` is not populated on the context
                    // but it is on the request object,
                    // even though that shouldn't be possible according to TS
                    const query = req === null || req === void 0 ? void 0 : req.query;
                    const preview = isPreview(query);
                    let locale = '';
                    if (isMultilingualEnabled) {
                        // LocaleUtils expects the default Request type from express but we can't access that here
                        locale = Boolean(req) ? LocaleUtils.getLocale(req, defaultLocale, preview) : defaultLocale;
                    }
                    // Get pageModel fetched on the server from the response object
                    // If it doesn't exist, we should try to fetch the brPage
                    // (in case of client-side request or server failure)
                    let rehydratedPageModel = (res === null || res === void 0 ? void 0 : (ref = res.locals) === null || ref === void 0 ? void 0 : ref.pageModel) || pageModel;
                    let { visitor  } = ctx;
                    if (!rehydratedPageModel) {
                        try {
                            new URL(asPath, apiUrl);
                        } catch (error) {
                            console.error(`Invalid URL path ${asPath}`, error);
                            // Invalid URL
                            return {
                                statusCode: 400
                            };
                        }
                        try {
                            const brPage = yield BloomreachApi.getBrPage(apiUrl, asPath, {
                                locale,
                                headers: req === null || req === void 0 ? void 0 : req.headers,
                                connection: req === null || req === void 0 ? void 0 : req.connection,
                                isMultilingual: isMultilingualEnabled
                            });
                            rehydratedPageModel = parsePageModel(brPage);
                            if (!visitor) {
                                const v = brPage === null || brPage === void 0 ? void 0 : brPage.getVisitor();
                                if (v) {
                                    visitor = v;
                                }
                            }
                        } catch (error1) {
                            console.error('An error occurred while fetching the Bloomreach page:', error1);
                            return {
                                statusCode: 400
                            };
                        }
                    }
                    const pageRoot = getPageRoot(rehydratedPageModel);
                    const isContentPage = (pageRoot === null || pageRoot === void 0 ? void 0 : (ref1 = pageRoot.models) === null || ref1 === void 0 ? void 0 : ref1.pageType) === 'content-page';
                    ctx.pageModel = rehydratedPageModel;
                    ctx.visitor = visitor;
                    ctx.isContentPage = isContentPage;
                    ctx.query = query;
                    let pageProps = {};
                    if (PageComponent.getInitialProps) {
                        pageProps = swcHelpers.objectSpread({
                            isContentPage
                        }, (yield PageComponent.getInitialProps(ctx)));
                    }
                    if (!rehydratedPageModel) {
                        console.error(`WithBloomreach: Failed to fetch page for ${asPath}`);
                    }
                    return swcHelpers.objectSpread({}, pageProps, {
                        visitor,
                        locale,
                        path: asPath,
                        isMultilingualEnabled,
                        pageModel: rehydratedPageModel
                    });
                });
                return function(ctx) {
                    return _ref.apply(this, arguments);
                };
            }();
            return WithBloomreach;
        };
    };
}

export { withBloomreach };
//# sourceMappingURL=with-bloomreach.js.map
